/** Saved column mappings and role aliases. SPEC-0001 step 6. */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { MappingProfile } from '@picklii/shared';
import { AppShell } from '../components/AppShell';
import { ConfirmAction } from '../components/ConfirmAction';
import { api } from '../lib/api';
import { shortDate } from '../lib/format';

export function Profiles() {
	const navigate = useNavigate();
	const [profiles, setProfiles] = useState<MappingProfile[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [deleting, setDeleting] = useState<string | null>(null);

	async function remove(id: string) {
		setDeleting(id);
		setError(null);
		try {
			await api.deleteProfile(id);
			setProfiles((current) => (current ?? []).filter((p) => p.id !== id));
		} catch (cause) {
			setError(cause instanceof Error ? cause.message : 'Could not delete that mapping.');
		} finally {
			setDeleting(null);
		}
	}

	useEffect(() => {
		api.listProfiles().then(setProfiles, (cause: Error) => setError(cause.message));
	}, []);

	return (
		<AppShell title="Mapping profiles">
			<section className="col-span-12">
				{error !== null && (
					<div className="alert alert-error alert-soft" role="alert">
						<span>{error}</span>
					</div>
				)}

				<div className="card bg-base-200 border-base-300 border shadow-xs">
					<div className="card-body gap-4">
						<div>
							<h2 className="card-title text-base">Saved mappings</h2>
							<p className="text-base-content/70 text-sm">
								A profile remembers which of your columns is which for one source, plus the
								role aliases you've confirmed. The two exports come from different systems
								and ask for different fields, so each side has its own. Aliases accumulate
								into an asset: setup is paid once, and the weekly re-run stays a two-click
								operation. Every run also records its own mapping, so one can be saved
								after the fact from the run's results.
							</p>
						</div>

						{profiles !== null && profiles.length === 0 && (
							<p className="text-base-content/70 text-sm">
								No profiles yet. You can name a mapping while setting up a
								reconciliation, or save it afterwards from that run's results page —
								every run records the mapping it used, so the chance to save is never
								lost.
							</p>
						)}

						{profiles !== null && profiles.length > 0 && (
							<div className="overflow-x-auto">
								<table className="table">
									<thead>
										<tr>
											<th>Name</th>
											<th>Source</th>
											<th>Mapped columns</th>
											<th>Aliases</th>
											<th>Updated</th>
											<th />
										</tr>
									</thead>
									<tbody>
										{profiles.map((profile) => (
											<tr
												key={profile.id}
												tabIndex={0}
												role="link"
												aria-label={`Edit ${profile.name}`}
												className="hover:bg-base-300 focus-visible:bg-base-300 cursor-pointer transition-colors"
												onClick={() => navigate(`/profiles/${profile.id}`)}
												onKeyDown={(e) => {
													if (e.key === 'Enter' || e.key === ' ') {
														e.preventDefault();
														navigate(`/profiles/${profile.id}`);
													}
												}}>
												<td className="font-medium">{profile.name}</td>
												<td>
													<span className="badge badge-ghost badge-sm">
														{profile.sourceType === 'sold' ? 'Sold hours' : 'PSA budget'}
													</span>
												</td>
												<td className="tabular-nums">{Object.keys(profile.columns).length}</td>
												<td className="tabular-nums">{profile.aliases.length}</td>
												<td>{shortDate(profile.updatedAt ?? '')}</td>
												<td
													className="text-right"
													onClick={(e) => e.stopPropagation()}
													onKeyDown={(e) => e.stopPropagation()}>
													<ConfirmAction
														compact
														label="Delete"
														confirmLabel={`Delete the mapping “${profile.name}”?`}
														description="Runs that used it keep their own record of the columns they read; only the reusable mapping goes."
														busy={deleting === profile.id}
														onConfirm={() => void remove(profile.id ?? '')}
													/>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
					</div>
				</div>
			</section>
		</AppShell>
	);
}
